import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, SearchIcon, SlidersHorizontalIcon } from 'lucide-react';
import { FormConfig, FormData } from '../types';
import { FormSection } from '../components/FormSection';
import { LoanHeader } from '../components/LoanHeader';
import { MetricCards } from '../components/MetricCards';
import { SectionChecklist, type SectionProgress } from '../components/SectionChecklist';
import { SaveBar } from '../components/SaveBar';
import { AdminConfig } from './AdminConfig';
import { buildDefaultConfig, clearStoredConfig, computeHiddenFields, fieldCatalog, loadConfig, saveConfig } from '../utils/config';
function buildInitialValues(): FormData {
  const data: FormData = {};
  Object.values(fieldCatalog).forEach(f => {
    data[f.id] = f.value;
  });
  return data;
}
export function LoanForm() {
  const initial = useMemo(buildInitialValues, []);
  const [values, setValues] = useState<FormData>(initial);
  const [saved, setSaved] = useState<FormData>(initial);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [toast, setToast] = useState(false);
  const [config, setConfig] = useState<FormConfig>(() => loadConfig());
  const [view, setView] = useState<'record' | 'admin'>('record');
  useEffect(() => {
    saveConfig(config);
  }, [config]);
  const dirtyIds = useMemo(() => {
    const set = new Set<string>();
    Object.keys(values).forEach(id => {
      if (String(values[id]) !== String(saved[id])) set.add(id);
    });
    return set;
  }, [values, saved]);
  const hiddenFieldIds = useMemo(() => computeHiddenFields(config, values), [config, values]);
  const handleChange = (id: string, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaved(values);
      setSaving(false);
      setToast(true);
      setTimeout(() => setToast(false), 2600);
    }, 700);
  };
  const handleDiscard = () => setValues(saved);
  const handleResetConfig = () => {
    clearStoredConfig();
    setConfig(buildDefaultConfig());
  };
  const scrollToSection = (sectionId: string) => {
    document.getElementById(`section-${sectionId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Resolve the visible sections from admin config + rules + search.
  const q = query.trim().toLowerCase();
  const resolvedSections = config.sections.filter(s => !s.hidden).map(s => {
    const fields = s.fieldIds.map(id => fieldCatalog[id]).filter(f => f && !hiddenFieldIds.has(f.id)).filter(f => q ? f.label.toLowerCase().includes(q) : true);
    const hiddenCount = s.fieldIds.filter(id => hiddenFieldIds.has(id)).length;
    return {
      ...s,
      resolvedFields: fields,
      hiddenCount
    };
  }).filter(s => q ? s.resolvedFields.length > 0 : true);

  // Completion rail reflects every visible section's REQUIRED fields, ignoring search.
  const requiredIds = new Set(config.requiredFieldIds);
  const progressSections: SectionProgress[] = config.sections.filter(s => !s.hidden).map(s => ({
    id: s.id,
    title: s.title,
    icon: s.icon,
    fields: s.fieldIds.map(id => fieldCatalog[id]).filter(f => f && requiredIds.has(f.id) && !hiddenFieldIds.has(f.id)),
    hiddenCount: s.fieldIds.filter(id => requiredIds.has(id) && hiddenFieldIds.has(id)).length
  })).filter(s => s.fields.length > 0);
  if (view === 'admin') {
    return <AdminConfig config={config} values={values} onChange={setConfig} onExit={() => setView('record')} onReset={handleResetConfig} data-id="element-386" data-mp="3f141x3k0gbg2" />;
  }
  return <div className="min-h-full w-full bg-slate-100 pb-32" data-id="element-387" data-mp="23lyy1w8586uv">
      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:py-8" data-id="element-388" data-mp="23njr1het7hqm">
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-389" data-mp="2iwnh2kojv0ef">
          <div data-id="element-390" data-mp="2e4ne0jd6nrdm">
            <p className="text-sm font-medium text-slate-400" data-id="element-391" data-mp="2e43u4x3z2swy">Loans</p>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-id="element-392" data-mp="295am3k7gfu46">Loan Detail</h1>
          </div>

          <div className="flex w-full items-center gap-2 sm:w-auto" data-id="element-393" data-mp="2550b1t4t2dig">
            {config.showSearch && <div className="relative w-full sm:w-72" data-id="element-394" data-mp="31teu4cdi0ja1">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-395" data-mp="2wvdclvvq0s0o" />
                <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search fields…" aria-label="Search fields" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-800 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-396" data-mp="2stjg7zsk6a6b" />
              </div>}
            <button type="button" onClick={() => setView('admin')} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900" data-id="element-397" data-mp="3t31b9uut7bep">
              <SlidersHorizontalIcon className="h-4 w-4" data-id="element-398" data-mp="aq35367fbmc3" />
              Admin
            </button>
          </div>
        </div>

        {config.headerMetricIds.length > 0 && <div className="sticky top-0 z-20 -mx-4 mb-4 border-b border-slate-200 bg-slate-100/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6" data-id="element-399" data-mp="kmoahsncuzgg">
            <MetricCards values={values} metricIds={config.headerMetricIds} data-id="element-400" data-mp="2mdjcj285nuvm" />
          </div>}

        <div className={`grid grid-cols-1 gap-5 ${config.showChecklist ? 'lg:grid-cols-[minmax(0,1fr)_320px]' : ''}`} data-id="element-401" data-mp="2qev3bb2rmm5e">
          <div className="min-w-0 space-y-4" data-id="element-402" data-mp="3tqajev9b4ako">
            {config.showHeader && <LoanHeader values={values} data-id="element-403" data-mp="3orz9kkj0i02g" />}

            {resolvedSections.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-400" data-id="element-404" data-mp="6lkvmlkqtq42">
                {q ? `No fields match “${query}”.` : 'All sections are hidden by the current configuration.'}
              </div> : resolvedSections.map(section => <FormSection key={section.id} anchorId={`section-${section.id}`} title={section.title} icon={section.icon} fields={section.resolvedFields} columns={section.columns} density={config.density} defaultCollapsed={section.defaultCollapsed} hiddenCount={section.hiddenCount} requiredIds={requiredIds} values={values} dirtyIds={dirtyIds} onChange={handleChange} data-id="element-405" data-mp="q9bua6r0dvzc" />)}
          </div>

          {config.showChecklist && <SectionChecklist sections={progressSections} values={values} dirtyIds={dirtyIds} onSelect={scrollToSection} data-id="element-406" data-mp="18xcatpq2jm2b" />}
        </div>
      </div>

      <SaveBar count={dirtyIds.size} saving={saving} onSave={handleSave} onDiscard={handleDiscard} data-id="element-407" data-mp="zxzbvqzkmfvu" />

      <AnimatePresence data-id="element-408" data-mp="l92swyvz7igg">
        {toast && <motion.div initial={{
        opacity: 0,
        y: -12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -12
      }} className="fixed left-1/2 top-5 z-50 flex -translate-x-1/2 items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-xl" data-id="element-409" data-mp="l66vdbo1ivsj">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-400" data-id="element-410" data-mp="wozek7iml5w1" />
            All changes saved
          </motion.div>}
      </AnimatePresence>
    </div>;
}